import { useCallback, useEffect, useMemo, useState } from "react";
import type { MxWidgetType } from "@modules/mx/types";
import { useWidget } from "@modules/mx/queries";
import { isString } from "lodash";
import { useMxZeroState } from "@modules/mx/queries";
import LoadingIndicator from "@common/components/LoadingIndicator";
import { useQueryClient } from "react-query";
import { updateAccountBalance } from "@modules/networth/helpers";
import MxZeroStateAgreement from "./MxZeroStateAgreement";

const Widget = ({ widgetType }: { widgetType: MxWidgetType }) => {
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { isMxZeroState, isCheckingZeroState } = useMxZeroState();
  const baseUrlPattern = /^https?:\/\/[^\/]+/i;
  const iframeId = useMemo(
    () => `mx-widget-${(Math.random() + 1).toString(36)}`,
    []
  );
  const pingIntervalMs = 30000;

  const { data } = useWidget(widgetType, {
    enabled: !isCheckingZeroState,
  });
  const mxWidgetUrl = data?.url;

  const widgetBaseUrl =
    mxWidgetUrl?.url && mxWidgetUrl.url.match(baseUrlPattern);

  useEffect(() => {
    let infinitePing: ReturnType<typeof setInterval>;
    if (loaded && widgetBaseUrl) {
      infinitePing = setInterval(() => {
        const iframe = window.document.getElementById(
          iframeId
        ) as HTMLIFrameElement;
        if (iframe) {
          iframe?.contentWindow?.postMessage(
            JSON.stringify({
              type: "ping",
            }),
            widgetBaseUrl ? widgetBaseUrl[0] : ""
          );
        }
      }, pingIntervalMs);
    }
    return () => {
      if (infinitePing) {
        clearInterval(infinitePing);
      }
    };
  }, [iframeId, loaded, widgetBaseUrl]);

  const queryClient = useQueryClient();

  const handleMxEvent = useCallback(
    (event: MessageEvent) => {
      let { data } = event;
      if (event.data && isString(event.data)) {
        try {
          data = JSON.parse(event.data);
          console.debug("MX Event", data);
          if (data.type === "load") {
            setLoaded(true);
          }

          if (["created", "updated", "deleted"].includes(data.type)) {
            updateAccountBalance(data, queryClient);
          }
        } catch (e) {
          console.log("Unexpected JSON.parse error", e, event.data);
        }
      }
    },
    [queryClient]
  );

  useEffect(() => {
    window.addEventListener("message", handleMxEvent);
    return () => {
      window.removeEventListener("message", handleMxEvent);
    };
  }, [handleMxEvent]);

  if (!mxWidgetUrl?.url) {
    // return null;
    return <LoadingIndicator isVisible />;
  }

  if (!isCheckingZeroState && isMxZeroState && !acceptedAgreement) {
    return <MxZeroStateAgreement onAccept={() => setAcceptedAgreement(true)} />;
  }

  return (
    <iframe id={iframeId} src={mxWidgetUrl.url} width="100%" height="100%" />
  );
};

export default Widget;
